import { useState, type FormEvent } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { Check, Copy, Eye, KeyRound, Play, Plus, ShieldX } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Badge, Button, Card, ConfirmDialog, EmptyState, Field, Input, Modal, Notice, PageHeader, TableSkeleton } from "../components/ui";
import { errorMessage, formatDate } from "../lib/format";

const SCOPE_LABELS: Record<string, string> = {
  "check:read": "Vérification",
  "blacklist:read": "Export complet",
  "blacklist:write": "Ajout et levée",
};

type Endpoint = {
  cle: "check" | "blacklist";
  methode: string;
  chemin: string;
  scope: string;
  resume: string;
  parametres: Array<[string, string]>;
  exemple: string;
};

const ENDPOINTS: Endpoint[] = [
  {
    cle: "check",
    methode: "GET",
    chemin: "/v1/check",
    scope: "check:read",
    resume: "Vérifie si un joueur est blacklisté. Un identifiant suffit ; plusieurs affinent la réponse.",
    parametres: [
      ["epic_id", "Epic ID du joueur (32 caractères)."],
      ["discord_id", "Identifiant Discord numérique."],
      ["epic_name", "Pseudo Epic, indicatif seulement."],
      ["discord_name", "Pseudo Discord, indicatif seulement."],
      ["include_lifted", "1 pour recevoir aussi les sanctions levées dans « history »."],
    ],
    exemple: "epic_id=4bb3c7334ed94aa9bbbfde33a1cb195b",
  },
  {
    cle: "blacklist",
    methode: "GET",
    chemin: "/v1/blacklist",
    scope: "blacklist:read",
    resume: "Exporte la liste active, page par page. Suivez « next_cursor » jusqu’à ce qu’il soit nul.",
    parametres: [
      ["category", "cheat, team_up ou insultes_admins."],
      ["limit", "1 à 500, 200 par défaut."],
      ["cursor", "Curseur renvoyé par la page précédente."],
    ],
    exemple: "limit=5",
  },
];

/** Référence des routes exposées, avec un appel prêt à coller dans un terminal. */
function ApiDocs({ origine }: { origine: string }) {
  return (
    <Card>
      <div className="card-heading"><h2>Endpoints</h2></div>
      <p className="muted">
        Toutes les routes attendent l’en-tête <code className="inline-code">x-api-key</code> (ou
        <code className="inline-code">Authorization: Bearer …</code>). Chaque clé porte ses propres
        permissions et sa limite de débit ; un dépassement renvoie <code className="inline-code">429</code>
        avec un en-tête <code className="inline-code">Retry-After</code>.
      </p>
      <div className="endpoint-list">
        {ENDPOINTS.map((item) => (
          <article key={item.cle} className="endpoint">
            <header>
              <span className="endpoint__method">{item.methode}</span>
              <code>{item.chemin}</code>
              <Badge tone="rose">{SCOPE_LABELS[item.scope] ?? item.scope}</Badge>
            </header>
            <p>{item.resume}</p>
            <dl className="endpoint__params">
              {item.parametres.map(([nom, description]) => (
                <div key={nom}><dt>{nom}</dt><dd>{description}</dd></div>
              ))}
            </dl>
            <pre className="endpoint__curl">{`curl -H "x-api-key: wls_bl_…" \
  "${origine}${item.chemin}?${item.exemple}"`}</pre>
          </article>
        ))}
      </div>
    </Card>
  );
}

/** Interroge l'API depuis le navigateur pour vérifier une clé sans quitter la page. */
function ApiTester() {
  const [endpoint, setEndpoint] = useState<Endpoint>(ENDPOINTS[0]);
  const [cle, setCle] = useState("");
  const [requete, setRequete] = useState(ENDPOINTS[0].exemple);
  const [reponse, setReponse] = useState<{ statut: number; corps: string } | null>(null);
  const [enCours, setEnCours] = useState(false);

  const tester = async () => {
    setEnCours(true); setReponse(null);
    const url = `${endpoint.chemin}${requete.trim() ? `?${requete.trim().replace(/^\?/, "")}` : ""}`;
    try {
      const res = await fetch(url, { headers: { "x-api-key": cle.trim(), Accept: "application/json" } });
      const texte = await res.text();
      let corps = texte;
      try { corps = JSON.stringify(JSON.parse(texte), null, 2); } catch { /* Réponse non JSON : affichée telle quelle. */ }
      setReponse({ statut: res.status, corps });
    } catch (reason) {
      setReponse({ statut: 0, corps: reason instanceof Error ? reason.message : "Requête impossible." });
    } finally { setEnCours(false); }
  };

  return (
    <Card>
      <div className="card-heading"><h2>Tester une clé</h2></div>
      <p className="muted">L’appel part de ce navigateur vers votre propre serveur. Il est tracé dans les Logs comme n’importe quel appel d’intégration.</p>
      <div className="tester-grid">
        <Field label="Endpoint">
          <select className="input" value={endpoint.cle}
            onChange={(event) => {
              const suivant = ENDPOINTS.find((item) => item.cle === event.target.value) ?? ENDPOINTS[0];
              setEndpoint(suivant); setRequete(suivant.exemple); setReponse(null);
            }}>
            {ENDPOINTS.map((item) => <option key={item.cle} value={item.cle}>{item.methode} {item.chemin}</option>)}
          </select>
        </Field>
        <Field label="Clé API" hint="Collez une clé wls_bl_… disposant de la permission requise.">
          <Input value={cle} onChange={(event) => setCle(event.target.value)} type="password" autoComplete="off" placeholder="wls_bl_…" />
        </Field>
        <Field label="Paramètres">
          <Input value={requete} onChange={(event) => setRequete(event.target.value)} spellCheck={false} placeholder={endpoint.exemple} />
        </Field>
      </div>
      <div className="button-row">
        <Button onClick={() => void tester()} loading={enCours} disabled={!cle.trim()}><Play size={15} /> Envoyer la requête</Button>
      </div>
      {reponse && (
        <div className="tester-response">
          <div className="tester-response__head">
            <Badge tone={reponse.statut >= 200 && reponse.statut < 300 ? "success" : "danger"}>
              {reponse.statut === 0 ? "Échec réseau" : `HTTP ${reponse.statut}`}
            </Badge>
            <code>{endpoint.methode} {endpoint.chemin}{requete.trim() ? `?${requete.trim()}` : ""}</code>
          </div>
          <pre>{reponse.corps}</pre>
        </div>
      )}
    </Card>
  );
}

/** Affiche un secret en clair, avec copie en un clic. */
export function RevealedSecret({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <div className="secret-panel">
      <code>{value}</code>
      <Button variant="secondary" onClick={() => void navigator.clipboard.writeText(value).then(() => { setCopied(true); setFailed(false); }, () => setFailed(true))}>
        {copied ? <><Check size={15} /> Copié</> : <><Copy size={15} /> Copier</>}
      </Button>
      {failed && <Notice tone="error">Copie impossible depuis ce navigateur : sélectionnez le texte ci-dessus.</Notice>}
    </div>
  );
}

function CreateKeyModal({ onClose }: { onClose: () => void }) {
  const create = useAction(api.apiKeys.create);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [secret, setSecret] = useState("");
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const scopes: Array<"check:read" | "blacklist:read" | "blacklist:write"> = [];
    if (form.get("check") === "on") scopes.push("check:read");
    if (form.get("list") === "on") scopes.push("blacklist:read");
    if (form.get("write") === "on") scopes.push("blacklist:write");
    setLoading(true); setError("");
    try {
      const result = await create({
        name: String(form.get("name")),
        scopes,
        rateLimitPerMinute: Number(form.get("rate")),
      });
      setSecret(result.key);
    } catch (reason) { setError(errorMessage(reason)); }
    finally { setLoading(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(secret); setCopied(true); setCopyFailed(false); }
    catch { setCopyFailed(true); }
  };

  return (
    <Modal
      title={secret ? "Clé créée" : "Créer une clé API"}
      description={secret ? undefined : "Accordez uniquement les permissions nécessaires à l’intégration."}
      onClose={onClose}
      // Tant que la clé est affichée, aucune fermeture accidentelle : elle est irrécupérable.
      dismissible={!secret}
    >
      {secret ? (
        <div className="secret-panel">
          <Notice tone="warning">Cette clé n’apparaîtra plus. Copiez-la maintenant.</Notice>
          <code>{secret}</code>
          <Button variant="secondary" onClick={() => void copy()}>
            {copied ? <><Check size={15} /> Clé copiée</> : <><Copy size={15} /> Copier la clé</>}
          </Button>
          {copyFailed && <Notice tone="error">Copie impossible depuis ce navigateur : sélectionnez la clé ci-dessus.</Notice>}
          <div className="modal-actions"><Button onClick={onClose}>J’ai copié la clé</Button></div>
        </div>
      ) : (
        <form onSubmit={submit} className="form-stack">
          <Field label="Nom de l’intégration"><Input name="name" required placeholder="Bot Discord production" autoFocus /></Field>
          <Field label="Limite par minute"><Input name="rate" type="number" min={1} max={1000} defaultValue={60} required /></Field>
          <div className="permission-list">
            <label><input type="checkbox" name="check" defaultChecked /><span><strong>Vérifier une identité</strong><small>GET · POST /v1/check</small></span></label>
            <label><input type="checkbox" name="list" /><span><strong>Lire toute la blacklist active</strong><small>GET /v1/blacklist</small></span></label>
            <label><input type="checkbox" name="write" /><span><strong>Ajouter et lever des sanctions</strong><small>POST /v1/blacklist · POST /v1/blacklist/lift</small></span></label>
          </div>
          {error && <Notice tone="error">{error}</Notice>}
          <div className="modal-actions">
            <Button variant="secondary" onClick={onClose}>Annuler</Button>
            <Button type="submit" loading={loading}>Générer</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export function ApiKeysPage() {
  const keys = useQuery(api.apiKeys.list);
  const revoke = useMutation(api.apiKeys.revoke);
  const reveal = useAction(api.apiKeys.reveal);
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState<{ name: string; key: string } | null>(null);
  const [revealError, setRevealError] = useState("");

  const showKey = async (id: Id<"apiKeys">, name: string) => {
    setRevealError("");
    try { setRevealed({ name, key: (await reveal({ id })).key }); }
    catch (reason) { setRevealError(errorMessage(reason)); }
  };
  const [confirmRevoke, setConfirmRevoke] = useState<{ _id: Id<"apiKeys">; name: string } | null>(null);

  return <>
    <PageHeader
      title="Clés API"
      description="Authentification par en-tête x-api-key, permissions séparées et limite de débit par clé."
      actions={<Button onClick={() => setOpen(true)}><Plus size={16} /> Nouvelle clé</Button>}
    />
    {keys === undefined ? <TableSkeleton /> : keys.length === 0 ? (
      <EmptyState title="Aucune clé API" description="Créez une clé dédiée pour votre première intégration." />
    ) : (
      <div className="key-grid">
        {keys.map((key) => (
          <Card key={key._id} className="key-card">
            <div className="key-card__icon"><KeyRound /></div>
            <div className="key-card__main">
              <div><h3>{key.name}</h3><code>{key.prefix}••••••••</code></div>
              <div className="scope-list">{key.scopes.map((scope) => <Badge key={scope} tone="rose">{SCOPE_LABELS[scope] ?? scope}</Badge>)}</div>
              <p>{key.rateLimitPerMinute} requêtes/min · créée le {formatDate(key.createdAt)}</p>
              {key.lastUsedAt && <small>Dernier appel : {formatDate(key.lastUsedAt)}</small>}
            </div>
            <div className="row-actions">
              {key.revealable && (
                <button type="button" className="icon-button" title="Revoir la clé" aria-label="Revoir la clé" onClick={() => void showKey(key._id, key.name)}>
                  <Eye size={17} />
                </button>
              )}
              {key.revokedAt ? <Badge tone="danger">Révoquée</Badge> : (
                <button type="button" className="icon-button icon-button--danger" title="Révoquer la clé" aria-label="Révoquer la clé" onClick={() => setConfirmRevoke({ _id: key._id, name: key.name })}>
                  <ShieldX size={18} />
                </button>
              )}
            </div>
          </Card>
        ))}
      </div>
    )}
    <ApiDocs origine={window.location.origin} />
    <ApiTester />
    {revealError && <Notice tone="error">{revealError}</Notice>}
    {revealed && (
      <Modal title={revealed.name} description="Clé API en clair. Elle reste consultable ici, chaque lecture étant tracée." onClose={() => setRevealed(null)}>
        <RevealedSecret value={revealed.key} />
        <div className="modal-actions"><Button onClick={() => setRevealed(null)}>Fermer</Button></div>
      </Modal>
    )}
    {open && <CreateKeyModal onClose={() => setOpen(false)} />}
    {confirmRevoke && (
      <ConfirmDialog
        title="Révoquer la clé"
        description="Les intégrations qui l’utilisent cesseront immédiatement de fonctionner. Cette action est définitive."
        target={confirmRevoke.name}
        confirmLabel="Révoquer"
        onConfirm={() => revoke({ id: confirmRevoke._id })}
        onClose={() => setConfirmRevoke(null)}
      />
    )}
  </>;
}
